import { CheckCircle, Close } from "@mui/icons-material";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import { useState } from "react";

type EditableTextFieldProps = {
  label?: string;
  value: string;
  onValueChanged?: (value: string) => void;
  isEditable?: boolean;
  fullWidth?: boolean;
};

export default function EditableTextField({
  label,
  value,
  onValueChanged,
  isEditable,
  fullWidth = false,
}: EditableTextFieldProps) {
  const [editValue, setEditValue] = useState<string>(value);
  const [editMode, setEditMode] = useState(false);

  const leaveEditMode = (applyChange: boolean) => {
    if (applyChange) {
      onValueChanged?.(editValue);
    }
    setEditMode(false);
  };

  return (
    <span
      style={{ display: "inline-flex", flexGrow: 1, minWidth: 0, maxWidth: "100%", width: fullWidth ? "100%" : undefined, alignItems: "center" }}
      onClick={(e) => e.stopPropagation()}
    >
      {!editMode && (
        <Chip
          label={value}
          title={value}
          sx={{
            width: fullWidth ? "100%" : undefined,
            justifyContent: "flex-start",
            maxWidth: "100%",
            height: "auto",
            bgcolor: "background.default",
            color: "text.primary",
            borderColor: "divider",
            borderRadius: 1,
            "& .MuiChip-label": {
              whiteSpace: isEditable ? "pre-wrap" : "nowrap",
              overflowWrap: isEditable ? "anywhere" : "normal",
              fontFamily: "monospace",
              lineHeight: 1.6,
              py: 0.5,
            },
          }}
          variant="outlined"
          onClick={() => {
            if (isEditable === true) {
              setEditValue(value);
              setEditMode(true);
            }
          }}
          color="info"
        />
      )}
      {editMode && (
        <Chip
          sx={{
            width: fullWidth ? "100%" : undefined,
            maxWidth: "100%",
            height: "auto",
            "& .MuiChip-label": { display: "flex", alignItems: "center", width: "100%", minWidth: 0, py: 0.5 },
          }}
          label={
            <>
              <TextField
                variant="standard"
                size="small"
                fullWidth={fullWidth}
                slotProps={{ htmlInput: { 'aria-label': label } }}
                sx={{ flex: 1, minWidth: 0 }}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    leaveEditMode(true);
                  } else if (e.key === "Escape") {
                    leaveEditMode(false);
                  }
                }}
              />
              <IconButton
                aria-label="apply edit"
                onClick={() => leaveEditMode(true)}
              >
                <CheckCircle />
              </IconButton>
              <IconButton
                aria-label="cancel edit"
                onClick={() => leaveEditMode(false)}
              >
                <Close />
              </IconButton>
            </>
          }
        />
      )}
    </span>
  );
}
